import {
  AdjustFeatures,
  AdjustTerms,
  DynamicScalar,
  PairedSalesAnalysisFeature,
  PairedSalesCustomAnalysisFeature,
  ToolkitProperty,
  ToolkitPropertyRowMap,
  adjustTermToFeature,
  dynamicScalarToString,
  isDynamicScalar,
  isUndefined,
} from '@aloft/toolkit-shared';
import { Typography } from '@aloft/toolkit-sui';

import { PLACEHOLDER } from '../../../../components/ToolkitPropertyTable/constants.ts';
import { getTPFieldSortBy } from '../../../../components/ToolkitPropertyTable/getTPFieldSortBy.ts';
import { CustomTPColumnDef } from '../../../../components/ToolkitPropertyTable/types.ts';
import { isPairedSalesCustomAnalysisFeature } from '../../utils/isPairedSalesCustomAnalysisFeature.ts';

type Row = { p1: ToolkitProperty; p2: ToolkitProperty };

const getStringValue = (value: DynamicScalar): string => {
  if (isUndefined(value)) return '-';
  if (typeof value === 'string') return value;
  return (isDynamicScalar(value) ? dynamicScalarToString(value) : null) ?? '-';
};

const maybeMakeToCustomTPColumnDef = <T extends Row>(
  term: PairedSalesAnalysisFeature,
): CustomTPColumnDef<T, [any, any]> | null => {
  if (isPairedSalesCustomAnalysisFeature(term)) return makeCustomFeatureDef(term);

  const termInfo = AdjustTerms[term];
  if (termInfo.isManual || termInfo.featureKey === 'additionalFeature') return null;

  const featureKey = adjustTermToFeature(term);
  if (isUndefined(featureKey)) return null;
  if (featureKey === 'listToSale') return null;

  const featureInfo = AdjustFeatures[featureKey];
  const propertyKey = featureInfo.propertyKeys?.[0];
  if (!propertyKey) return null;

  const sortBy = getTPFieldSortBy(ToolkitPropertyRowMap[propertyKey].type);

  return {
    id: `custom$${term}`,
    title: termInfo.label,
    selector: (x) => [featureInfo.getAnalysisValue(x.p1), featureInfo.getAnalysisValue(x.p2)],
    toCell: (x) => {
      const p1 = getStringValue(x[0]);
      const p2 = getStringValue(x[1]);

      return (
        <span style={{ display: 'flex', flexDirection: 'column' }}>
          <span>
            <Typography variant="tableH2" truncate>
              {p1}
            </Typography>
          </span>
          <span>
            <Typography variant="tableH2" truncate>
              {p2}
            </Typography>
          </span>
        </span>
      );
    },
    __dangerOverrideSortBy: (rowA, rowB) => {
      const a = featureInfo.getAnalysisValue(rowA.original.p1);
      const b = featureInfo.getAnalysisValue(rowB.original.p2);

      if (a === undefined) {
        return 1;
      }
      if (b === undefined) {
        return -1;
      }

      return sortBy(a, b);
    },
  };
};

const makeCustomFeatureDef = <T extends Row>(
  term: PairedSalesCustomAnalysisFeature,
): CustomTPColumnDef<T, [any, any]> | null => ({
  id: `custom$${term.name}`,
  title: term.name,
  selector: (x) => [x.p1.custom, x.p2.custom],
  toCell: (x) => {
    const p1 = getStringValue(x[0]) || PLACEHOLDER;
    const p2 = getStringValue(x[1]) || PLACEHOLDER;
    return (
      <span style={{ display: 'flex', flexDirection: 'column' }}>
        <span>
          <Typography variant="tableH2" truncate>
            {p1}
          </Typography>
        </span>
        <span>
          <Typography variant="tableH2" truncate>
            {p2}
          </Typography>
        </span>
      </span>
    );
  },
  __dangerOverrideSortBy: (rowA, rowB) => {
    const a = rowA.original.p1.custom;
    const b = rowB.original.p2.custom;

    if (a === undefined) {
      return 1;
    }
    if (b === undefined) {
      return -1;
    }

    return getTPFieldSortBy('string')(a, b);
  },
});

export const getColumnDefForTerm = <T extends Row>(
  term: PairedSalesAnalysisFeature,
): [CustomTPColumnDef<T, [any, any]>] | [] => {
  const def = maybeMakeToCustomTPColumnDef(term);
  return def ? [def as unknown as CustomTPColumnDef<T, [any, any]>] : [];
};
